import { useEffect, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";

interface LayoutProps {
  children?: ReactNode;
}

const pageTitles: Record<string, string> = {
  "/": "AI Outbound Sales Systems for B2B Tech",
  "/how-it-works": "How Our Outbound Sales System Works",
  "/solutions": "B2B Sales Systems & Solutions",
  "/technology/ai-automation": "AI & Sales Automation",
  "/technology/data-prospecting": "B2B Data & Prospecting",
  "/technology/outbound-infrastructure": "Outbound Sales Infrastructure",
  "/why-uniqreach": "Why UniqReach",
  "/results": "Measuring Sales Outcomes",
  "/about": "About UniqReach",
  "/faq": "Frequently Asked Questions",
  "/book": "Book a Strategy Call",
  "/contact": "Contact UniqReach",
  "/privacy": "Privacy Policy",
  "/terms": "Terms of Service",
};

export function Layout({ children }: LayoutProps) {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const title = `${pageTitles[pathname] || "Sales Systems for B2B Tech"} | UniqReach`;
    document.title = title;
    document
      .querySelector('meta[property="og:title"]')
      ?.setAttribute("content", title);
    document
      .querySelector('meta[name="twitter:title"]')
      ?.setAttribute("content", title);
    document
      .querySelector('link[rel="canonical"]')
      ?.setAttribute("href", `https://uniqreach.com${pathname}`);
    document
      .querySelector('meta[property="og:url"]')
      ?.setAttribute("content", `https://uniqreach.com${pathname}`);
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
    else window.scrollTo({ top: 0, behavior: "instant" });
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduceMotion || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 },
    );
    document
      .querySelectorAll(
        ".section .card, .section .card-elevated, .section .section-heading",
      )
      .forEach((element) => {
        element.classList.add("motion-ready");
        observer.observe(element);
      });
    return () => observer.disconnect();
  }, [pathname, hash]);
  return (
    <div className="flex flex-col min-h-screen bg-background">
      <Navbar />
      <main className="flex-1 pt-16 md:pt-20" id="main-content" tabIndex={-1}>
        {children || <Outlet />}
      </main>
      <Footer />
    </div>
  );
}

export function BlankLayout({ children }: LayoutProps) {
  return (
    <div className="flex flex-col min-h-screen bg-background">
      <main className="flex-1" id="main-content">
        {children || <Outlet />}
      </main>
    </div>
  );
}
